import React, { useEffect, useRef, useState } from 'react';
// import { useHistory } from 'react-router-dom';
import Clock from '../../image/icons/map/ClockIcon.svg';
import ArrowLeftToRight from '../../image/icons/map/ArrowLeftToRight.svg';
import Dot from '../../image/icons/map/Dot.svg';
import TravelProgress from './TravelProgress';
import { type ContainerProps } from '../../types/TripPlanner';
import { TransportMode, TripService } from '../../services/TripService';
import Loader from '../Loader/Loader';
import CartIcon from '../../image/Cart.svg';
import TravelProgressTypeTwo from './TravelProgressTypeTwo';
import Car from '../../image/icons/map/tripsDetailsTwo/CarIcon.svg';
import Train from '../../image/icons/map/tripsDetailsTwo/TrainIcon.svg';
import Tram from '../../image/icons/map/tripsDetailsTwo/TramIcon.svg';
import Bus from '../../image/icons/map/tripsDetailsTwo/BusIcon.svg';
import Walk from '../../image/icons/map/tripsDetailsTwo/WalkingIcon.svg';
import Cycle from '../../image/icons/map/tripsDetailsTwo/CycleIcon.svg';
import MyPosition from '../../image/icons/map/tripsDetailsTwo/MyPositionIcon.svg';
import Destination from '../../image/icons/map/tripsDetailsTwo/DestinationIcon.svg';
import HeLeman from '../../image/icons/map/tripsDetailsTwo/HeLemanIcon.svg';
import BlaBlaDaily from '../../image/icons/map/tripsDetailsTwo/BlabladailyIcon.svg';
import Donkey from '../../image/icons/map/tripsDetailsTwo/DonkeyIcon.svg';
import Dash from '../../image/icons/map/tripsDetailsTwo/DashIcon.svg';
import OneDash from '../../image/icons/map/tripsDetailsTwo/OneDashIcon.svg';
import Correspondance from '../../image/icons/map/tripsDetailsTwo/Correspondance.svg';
import ArrowOpenIcon from '../../image/icons/map/tripsDetailsTwo/ArrowTopGray.svg';
import ArrowCloseIcon from '../../image/icons/map/tripsDetailsTwo/ArrowBottomRed.svg';
import NoResultIcon from '../../image/icons/trips/NoResult.svg';
import GrayClockIcon from '../../image/icons/trips/GrayClockIcon.svg';
import { timeToString } from '../../tools/TimeRelated';
import CarSelection from '../Forms/CarSelection';
import { useIonToast } from '@ionic/react';
import { type UnifiedLegDTO, type UnifiedItineraryDTO } from '../../types/Itinerary';
import { useMain } from '../../store/StoredContext';
import { useLocationContext } from '../../store/LocationContext';
import { useEphemeral } from '../../store/EphemeralContext';
import { typeToOrder } from '../../tools/Itinerary';
import { isPlatform } from '@ionic/core';

const tripService = new TripService();

// Fonction pour renvoyer l'icône correspondante au mode de transport
const SwitchIcon = (mode: string) => {
  // console.log(`Switch Icon ${mode}`);
  switch (mode.toLowerCase()) {
    case 'myposition':
      return <img src={MyPosition} className="w-[15px]" />;
    case 'dashes':
      return (
        <div>
          <img src={Dash} />
        </div>
      );
    case 'onedashe':
      return (
        <div>
          <img src={OneDash} />
        </div>
      );
    case 'corres':
      return <img src={Correspondance} className="w-[15px]" />;
    case 'car':
      return <img src={Car} className="w-[15px]" />;
    case 'train':
      return <img src={Train} className="w-[15px]" />;
    case 'rail':
      return <img src={Train} className="w-[15px]" />;
    case 'tram':
    case 'subway':
      return <img src={Tram} className="w-[15px]" />;
    case 'bus':
    case 'coach':
      return <img src={Bus} className="w-[15px]" />;
    case 'walk':
      return <img src={Walk} className="w-[15px]" />;
    case 'bicycle':
      return <img src={Cycle} className="w-[15px]" />;
    case 'rentbike':
      return <img src={Donkey} className="w-[25px]" />;
    case 'blabladaily':
      return <img src={BlaBlaDaily} className="w-[25px]" />;
    case 'destination':
      return <img src={Destination} className="w-[15px]" />;
    case 'heleman':
      return <img src={HeLeman} className="w-[25px]" />;
    default:
      return null;
  }
};

// Modes de transport considérés comme des transits
const allTransits = ['bus', 'coach', 'train', 'tram', 'rail', 'subway', 'heLeman', 'gondola'];

function formatSeconds(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  let result = '';

  if (hours > 0) {
    result += `${hours} heure${hours > 1 ? 's ' : ' '}`;
  }
  if (minutes > 0) {
    result += `${minutes} min `;
  }
  if (remainingSeconds > 0 || seconds === 0) {
    result += `${remainingSeconds.toString()} s`;
  }
  return result.trim();
}

// Formater les secondes en minutes lisibles par un humain
function formatSecondsToMinutes(seconds: number): string {
  // console.log(`formatSecondsToMinutes ${seconds}`);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  let result = '';

  if (hours > 0) {
    result += `${hours} heure${hours > 1 ? 's ' : ' '}`;
  }
  if (minutes > 0) {
    result += `${minutes} minute${minutes > 1 ? 's ' : ' '}`;
  }
  if ((remainingSeconds > 0 || seconds === 0) && minutes === 0) {
    result += `${remainingSeconds.toString()} secondes`;
  }
  return result.trim();
}

/**
 * Calculer le temps de correspondance entre deux étapes de transit.
 * @param {UnifiedLegDTO} leg L'étape de transit actuelle.
 * @param {UnifiedLegDTO} nextLeg L'étape de transit suivante.
 * @returns {string} Le temps de correspondance en minutes.
 */
function calculateCorrespondanceTime(leg: UnifiedLegDTO, nextLeg: UnifiedLegDTO): string {
  const legEndTime = new Date(leg.endTime);
  const nextLegStartTime = new Date(nextLeg.startTime);

  const differenceInMilliseconds = nextLegStartTime.getTime() - legEndTime.getTime();

  const differenceInMinutes = Math.floor(differenceInMilliseconds / (1000 * 60));

  return `${differenceInMinutes} minutes`;
}

const TripList: React.FC<ContainerProps> = ({
  selectedAddress,
  date,
  setShowArrowTop,
  setTripListHeight,
  selectedTrip,
  setSelectedTrip,
  ITakeMyCar,
  setITakeMyCar,
  departureSelectedAddress,
  tripListHeight,
  isEditedDate,
  ITakeMyCarData,
  setITakeMyCarData,
  itineraries,
  setItineraries,
  selectedItinerary,
  setSelectedItinerary,
  setIsMerge,
  selectedInputs,
  setVisible,
  goItineraries,
  setGoItineraries,
  backupReturnItineraries,
  setBackupReturnItineraries,
  visible,
  journeys,
  setJourneys,
  setSelectedLeg,
  isModalOpen
}) => {
  // const history = useHistory();
  const [loading, setLoading] = useState<boolean>(false);
  const [legDetails, setLegDetails] = useState<boolean>(false);
  const requestId = useRef(0);
  const { userLocation } = useLocationContext();

  const [searchLocation, setSearchLocation] = useState<{ latitude: number; longitude: number }>(userLocation);

  const {
    state: { bikeFactor, currency }
  } = useMain();
  const {
    state: { trackingMode },
    setLoadingItinerary
  } = useEphemeral();

  const [prevBikeFactorValues, setPrevBikeFactorValues] = useState(bikeFactor); // Local state for previous values
  const [shouldReload, setShouldReload] = useState(false); // Trigger for reload

  useEffect(() => {
    if (!visible && !trackingMode) {
      setSearchLocation(userLocation);
    }
  }, [trackingMode, userLocation, visible]);

  useEffect(() => {
    // Compare current bikeFactor with previous
    const valuesChanged = JSON.stringify(bikeFactor) !== JSON.stringify(prevBikeFactorValues);

    // Trigger reload if modal is closed and values have changed
    if (!isModalOpen && valuesChanged) {
      // console.log('Values changed, triggering reload...');
      setShouldReload(true); // Set reload trigger
      setPrevBikeFactorValues(bikeFactor); // Update previous bikeFactor values
    }
  }, [isModalOpen, bikeFactor, prevBikeFactorValues]); // Dependency array: modal state and bikeFactor changes

  useEffect(() => {
    // Log debug information
    // console.log('Modal state:', isModalOpen);
    // console.log('Bike factor values:', bikeFactor);
    // console.log('Previous bike factor values:', prevBikeFactorValues);

    // If shouldReload is true, execute your reload logic here
    if (shouldReload) {
      // console.log('Executing reload logic...');
      // Your reload logic here (e.g., API calls)
      setShouldReload(false); // Reset reload trigger after logic is executed
    }
  }, [bikeFactor, isModalOpen, prevBikeFactorValues, shouldReload]); // Dependency: Executes only when shouldReload changes

  // Pour les messages toastCorrespondence
  const [present] = useIonToast();

  // Charger les données des trajets lorsque les inputs changent
  useEffect(() => {
    if (trackingMode) return;
    const loadData = async () => {
      // console.log('Loading = true');
      setVisible(true);
      setLoading(true); // Start loading
      setLoadingItinerary(true);
      setJourneys([]);

      let nbToReceive = 0;
      let nbItem = 0;
      const id = ++requestId.current;
      try {
        for (const mode in TransportMode) {
          nbToReceive++;
          tripService
            .listTrips(
              {
                fromlat: selectedInputs.departure.lat === -1 ? searchLocation.latitude.toString() : selectedInputs.departure.lat.toString(),
                fromlon: selectedInputs.departure.lon === -1 ? searchLocation.longitude.toString() : selectedInputs.departure.lon.toString(),
                tolat: selectedInputs.arrival.lat === -1 ? searchLocation.latitude.toString() : selectedInputs.arrival.lat.toString(),
                tolon: selectedInputs.arrival.lon === -1 ? searchLocation.longitude.toString() : selectedInputs.arrival.lon.toString(),
                date,
                timeFactor: bikeFactor.timeFactor,
                slopeFactor: bikeFactor.slopeFactor,
                safetyFactor: bikeFactor.safetyFactor,
                isPersonalBike: bikeFactor.isPersonalBike
              },
              mode as TransportMode
            )
            .then((resp) => {
              /** Annule le traitement si on a lancé une autre requête entre temps */
              if (id !== requestId.current) {
                return;
              }
              nbToReceive--;
              nbItem += resp.length;
              setJourneys((oldResp) => [...oldResp, ...resp].sort((a, b) => typeToOrder(a.type) - typeToOrder(b.type)));
              if (nbItem > 0) {
                setLoading(false);
              }
              if (nbToReceive === 0) {
                setLoadingItinerary(false);
                setLoading(false);
              }
            })
            .catch((e) => {
              /** Annule le traitement si on a lancé une autre requête entre temps */
              if (id !== requestId.current) {
                return;
              }
              setVisible(true);
              setLoading(false);
              setLoadingItinerary(false);
              void present({
                message: 'Erreur de serveur, réessayez plus tard !',
                duration: 500,
                position: 'bottom',
                color: 'danger'
              });
              console.error(e);
            });
        }

        const myCarProposition = await tripService.getMyCarItinerary({
          source: {
            lat: selectedInputs.departure.lat === -1 ? searchLocation.latitude : selectedInputs.departure.lat,
            lon: selectedInputs.departure.lon === -1 ? searchLocation.longitude : selectedInputs.departure.lon
          },
          destination: {
            lat: selectedInputs.arrival.lat === -1 ? searchLocation.latitude : selectedInputs.arrival.lat,
            lon: selectedInputs.arrival.lon === -1 ? searchLocation.longitude : selectedInputs.arrival.lon
          },
          type: 'default',
          startDateTime: date
        });

        setITakeMyCarData(myCarProposition.data);
      } catch (err) {
        console.error(err);
        setVisible(false);
        void present({
          message: 'Erreur de serveur, réessayez plus tard !',
          duration: 500,
          position: 'bottom',
          color: 'danger'
        });
      }
    };

    // Validate coordinates before making API calls
    if (selectedInputs.departure.lat !== 0 && selectedInputs.departure.lon !== 0 && selectedInputs.arrival.lat !== 0 && selectedInputs.arrival.lon !== 0) {
      setITakeMyCar(false);
      void loadData();
    } else {
      setJourneys([]); // Clear journeys if coordinates are invalid
    }
  }, [
    bikeFactor.isPersonalBike,
    bikeFactor.safetyFactor,
    bikeFactor.slopeFactor,
    bikeFactor.timeFactor,
    date,
    isEditedDate,
    present,
    searchLocation.latitude,
    searchLocation.longitude,
    selectedInputs.arrival.lat,
    selectedInputs.arrival.lon,
    selectedInputs.departure.lat,
    selectedInputs.departure.lon,
    setITakeMyCar,
    setITakeMyCarData,
    setJourneys,
    setLoadingItinerary,
    setVisible,
    trackingMode
  ]);

  /**
   * Masquer les détails des étapes lorsqu'on revient en arrière
   */
  useEffect(() => {
    if (ITakeMyCar || selectedTrip === null) setLegDetails(false);
  }, [ITakeMyCar, selectedTrip]);

  /**
   * Réinitialiser la liste des trajets lorsqu'on ferme la recherche
   */
  useEffect(() => {
    if (!visible) {
      setJourneys([]);
    }
  }, [setJourneys, visible]);

  // Gérer le clic sur un trajet pour sélectionner le trajet et ajuster l'interface utilisateur en conséquence
  const handleJourneyClick = (journey: UnifiedItineraryDTO) => {
    setSelectedTrip(journey);
    setShowArrowTop(true);
    setTripListHeight(50);
    setIsMerge(true);
  };

  // Formater les secondes en une durée lisible par un humain

  if (loading) {
    return <Loader />;
  }

  return (selectedTrip == null || selectedTrip === undefined) && !ITakeMyCar ? (
    loading ? (
      <Loader />
    ) : (
      <div id="TripList" className="flex flex-col" style={{ maxHeight: `${tripListHeight - 5}vh` }}>
        {/* Aucun trajet disponible */}
        {journeys.length === 0 && !loading ? (
          <div className="w-full my-4">
            <div className="flex justify-center">
              <img src={NoResultIcon} className="mb-5" />
            </div>
            <p className="text-center text-ionic-gray">{`Aucun itinéraire trouvé. ${selectedInputs.arrival.lat === selectedInputs.departure.lat && selectedInputs.arrival.lon === selectedInputs.departure.lon ? 'Veuillez sélectionner des adresses différentes' : 'Réessayez plus tard ou prenez votre propre voiture.'}`}</p>
            {!(selectedInputs.arrival.lat === selectedInputs.departure.lat && selectedInputs.arrival.lon === selectedInputs.departure.lon) && (
              <div className="flex justify-center py-5">
                <span className="p-2 px-5 rounded-lg text-center bg-[#76919B] text-white">Me Notifier</span>
              </div>
            )}
          </div>
        ) : (
          <div className="flowrideScrollBar overflow-auto" style={{ height: '70%' }}>
            {journeys.map((journey, index) => {
              return (
                <div
                  key={index}
                  className={`cursor-pointer mb-3 border-ionic-light-gray ${index < journeys.length - 1 ? 'border-b-2' : ''} pb-2`}
                  onClick={() => {
                    handleJourneyClick(journey);
                    setLegDetails(true);
                  }}
                >
                  <div className="flex items-center">
                    {/* Heure et espace de délai */}
                    <div className="flex justify-center items-center">
                      <img src={Clock} alt="Petite horloge" />
                      <h4 className="pl-2 text-xs text-black flex">
                        {formatSeconds(journey.duration)}
                        <img src={Dot} className="ml-1 mr-1" />
                        <b>{new Date(journey.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}</b>
                        <img src={ArrowLeftToRight} className="ml-1 mr-1 w-3" />
                        {new Date(journey.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                      </h4>
                    </div>
                    {/* Prix */}
                    <span className="ml-auto border-solid border-2 border-green-train-tram text-green-train-tram text-xs font-bold py-1 px-2 rounded-lg">
                      {`${journey.price} ${currency}`}
                    </span>
                  </div>
                  {/* Détail du trajet */}
                  <div className="pt-2">
                    <TravelProgress legs={journey.legs} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Proposition de prendre ma voiture */}
        {ITakeMyCarData !== null && (
          <div
            style={{ height: '30%' }}
            className="shadow-top-2 pt-3 cursor-pointer -mx-4 px-4"
            onClick={() => {
              setITakeMyCar(true);
              setTripListHeight(50);
            }}
          >
            <div className="flex items-center">
              {/* Heure et espace de délai */}
              <div className="flex justify-center items-center">
                <img src={Clock} alt="Petite horloge" className="h-5 w-5" />
                <h4 className="pl-2 text-xs text-black flex">
                  {formatSeconds(ITakeMyCarData.duration / 1000)}
                  <img src={Dot} className="ml-1 mr-1" />
                  <b>{new Date(ITakeMyCarData.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}</b>
                  <img src={ArrowLeftToRight} className="ml-1 mr-1 w-3" />
                  {new Date(ITakeMyCarData.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                </h4>
              </div>
              {/* Prix */}
              <span className="ml-auto border-solid border-2 border-red-500 text-red-500 text-xs font-bold py-1 px-2 rounded-lg">{`${ITakeMyCarData.cost} ${currency}`}</span>
            </div>
            {/* Détail du trajet */}
            <div className="pt-2">
              <TravelProgress legs={ITakeMyCarData.legs} isMyCar={true} />
            </div>
          </div>
        )}
      </div>
    )
  ) : !ITakeMyCar && selectedTrip != null ? (
    <div className="flex flex-col" style={{ maxHeight: `${tripListHeight === 22 ? 29 : tripListHeight - 5}vh` }}>
      <div className="flowrideScrollBar overflow-auto" style={{ height: '70%' }}>
        <div className="flex mb-2 items-center">
          {/* Heure et espace de délai */}
          <div className="flex justify-center items-center">
            <img src={Clock} alt="Petite horloge" />
            <h4 className="pl-2 text-xs text-black flex">
              {formatSeconds(selectedTrip.duration)}
              <img src={Dot} className="ml-1 mr-1" />
              {new Date(selectedTrip.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
              <img src={ArrowLeftToRight} className="ml-1 mr-1 w-3" />
              {new Date(selectedTrip.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
            </h4>
          </div>
          {/* Prix */}
          <span
            style={{ padding: '4px 6px 3px 6px' }}
            className="ml-auto border-solid border-2 border-green-train-tram text-green-train-tram text-xs font-bold flex justify-center items-center px-1 rounded-lg"
          >
            {`${selectedTrip.price} ${currency}`}
          </span>
        </div>
        <div
          onClick={() => {
            setLegDetails((prev) => !prev);
            // setTripListHeight(tripListHeight === 50 ? 45 : 50);
          }}
          className="cursor-pointer"
        >
          <TravelProgressTypeTwo legs={selectedTrip.legs} />
          {/* Plus de détails */}
          <div className="w-full flex justify-end pt-3">
            <span className={`text-sm ${legDetails ? 'text-ionic-danger' : 'text-gray-500'} bg-white rounded-md flex px-1.5 py-1 justify-center items-center`}>
              {legDetails ? 'Moins' : 'Plus'}
              {legDetails ? <img src={ArrowCloseIcon} className="ml-3" /> : <img src={ArrowOpenIcon} className="ml-3" />}
            </span>
          </div>
        </div>
        {legDetails && (
          <div className="border-t-ionic-light-gray border-t mt-3 pt-3">
            <div className="flex items-center">
              <div className="w-1/12 flex items-center justify-center">{SwitchIcon('myposition')}</div>
              <div className="flex w-11/12">
                <span className="text-xs max-w-[95%] text-ionic-secondary font-semibold">
                  {departureSelectedAddress != null ? departureSelectedAddress.address : 'Votre Position'}
                </span>
                <span className="ml-auto text-right text-xs text-ionic-secondary font-semibold">{timeToString(selectedTrip.legs[0].startTime)}</span>
              </div>
            </div>
            <div className="flex items-center mt-2 mb-2">
              <div className="w-1/12 flex items-center justify-center">{SwitchIcon('dashes')}</div>
              <div className="flex w-11/12 border-t-ionic-light-gray border-t"></div>
            </div>
            {selectedTrip.legs.length > 0 &&
              selectedTrip.legs.map((leg, index) => {
                // Afficher chaque étape du trajet
                return (
                  <div
                    key={index}
                    onClick={() => {
                      setSelectedLeg(leg);
                    }}
                  >
                    <div className="flex items-center mt-2 mb-2">
                      <div className="w-1/12 flex items-center justify-center">{SwitchIcon(leg.mode)}</div>
                      <div className="flex w-11/12">
                        <span className="text-xs max-w-[95%] text-ionic-secondary font-semibold">
                          {leg.mode === 'walk' ? `${formatSecondsToMinutes(leg.duration)} de marche` : leg.from?.name != null ? leg.from?.name : 'Départ'}
                          {leg.mode === 'rentBike' && leg.from?.vehicleRentalStation?.rentalUris?.android != null && (
                            <span className="text-xs text-ionic-secondary font-semibold">
                              {' - '}
                              <a href={leg.from.vehicleRentalStation.rentalUris?.android} target="_blank" rel="noreferrer">
                                (Louer un vélo)
                              </a>
                            </span>
                          )}
                          {leg.mode === 'blabladaily' && leg.from?.vehicleRentalStation?.rentalUris?.web != null && (
                            <span className="text-xs text-ionic-secondary font-semibold">
                              {' - '}
                              <a href={leg.from.vehicleRentalStation.rentalUris?.web} target="_blank" rel="noreferrer">
                                (Réserver un covoiturage)
                              </a>
                            </span>
                          )}
                          {allTransits.includes(leg.mode) && leg.headsign != null && ` (${leg.headsign}) `}
                          {leg.from?.dock !== '' && leg.from?.dock !== undefined ? ` (quai ${leg.from?.dock})` : ''}
                        </span>
                        {leg.mode !== 'walk' && (
                          <span className="ml-auto text-right text-xs text-ionic-secondary font-semibold">
                            {leg.from?.departure != null && leg.mode === 'blabladaily'
                              ? `${timeToString(Math.floor(leg.from?.departure / (5 * 60000)) * (5 * 60000))} -
                            ${timeToString(Math.ceil(leg.from?.departure / (5 * 60000)) * (5 * 60000))}`
                              : timeToString(leg.from?.departure ?? 0)}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Détails des étapes pour les transits */}
                    {allTransits.includes(leg.mode) && (
                      <div className="flex">
                        <div className="w-1/12 h-full flex items-center justify-center">
                          <span className="h-10 w-[3px] bg-ionic-orange"></span>
                        </div>
                        <div className="w-11/12">
                          <span className="flex items-center text-xs max-w-[95%] text-gray-500 mb-2">
                            <img src={GrayClockIcon} className="mr-1 w-3" />
                            {`Durée du trajet : ${leg.intermediateStops != null && leg.intermediateStops.length > 0 ? `${leg.intermediateStops.length} arréts (${formatSeconds(leg.duration)})` : formatSeconds(leg.duration)}`}
                            <span
                              className={`ml-2 rounded-md text-white px-1 ${leg.mode === 'bus' || leg.mode === 'coach' || leg.mode === 'heLeman' ? 'bg-[#d89c29]' : leg.mode === 'rail' || leg.mode === 'subway' || leg.mode === 'train' || leg.mode === 'tram' ? 'bg-[#4CAF50]' : 'bg-ionic-orange'}`}
                            >
                              {leg.routeShortName}
                            </span>
                            {(leg.agency?.gtfsId?.includes('881') ?? false) && (
                              <>
                                &nbsp;&nbsp;&nbsp;
                                <a
                                  href={isPlatform('ios') ? 'sms:788&body=tpg' : 'sms:788?body=tpg'}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-black underline"
                                >
                                  (Commander un billet)
                                </a>
                              </>
                            )}
                          </span>
                          {leg.intermediateStops != null && leg.intermediateStops.length === 0 && (
                            <div className="flex w-full">
                              <span className="text-xs max-w-[95%] text-ionic-secondary font-semibold">
                                {leg.to?.name != null ? leg.to?.name : 'Arrivée'}
                                {leg.to?.dock !== '' && ` (quai ${leg.to?.dock})`}
                              </span>
                              <span className="ml-auto text-right text-xs text-ionic-secondary font-semibold">
                                {leg.to?.departure != null && timeToString(leg.to?.departure)}
                              </span>
                            </div>
                          )}
                          {leg.intermediateStops != null && leg.intermediateStops.length > 0 && (
                            <div className="flex w-full">
                              <span className="text-xs max-w-[95%] text-ionic-secondary font-semibold">
                                {leg.to?.name != null ? leg.to?.name : 'Arrivée'}
                                {leg.to?.dock !== '' && ` (quai ${leg.to?.dock})`}
                              </span>
                              <span className="ml-auto text-right text-xs text-ionic-secondary font-semibold">
                                {leg.to?.departure != null && timeToString(leg.to?.departure)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Vérifier si l'étape actuelle et la suivante sont des transits pour afficher une correspondance */}
                    {allTransits.includes(leg.mode) && allTransits.includes(selectedTrip.legs[index + 1].mode) && (
                      <div>
                        <div className="w-1/12 flex items-center justify-center mt-1 mb-1">{SwitchIcon('onedashe')}</div>
                        <div className="flex items-center">
                          <div className="w-1/12 flex items-center justify-center">{SwitchIcon('corres')}</div>
                          <div className="flex w-11/12">
                            <span className="text-xs max-w-[95%] text-ionic-secondary font-semibold">
                              Correspondance de {calculateCorrespondanceTime(leg, selectedTrip.legs[index + 1])}{' '}
                            </span>
                          </div>
                        </div>
                        <div className="w-1/12 flex items-center justify-center mt-1">{SwitchIcon('onedashe')}</div>
                      </div>
                    )}
                    {/* Séparateur */}
                    <div className="flex items-center mb-2">
                      {leg.mode === 'walk' || leg.mode === 'bicycle' || leg.mode === 'rentBike' || leg.mode === 'blabladaily' ? (
                        <div className="w-1/12 flex items-center justify-center">{SwitchIcon('dashes')}</div>
                      ) : (
                        !allTransits.includes(selectedTrip.legs[index + 1].mode) && (
                          <div className="w-1/12 h-full flex items-center justify-center">
                            <span className="h-[20px] w-[3px] bg-ionic-orange"></span>
                          </div>
                        )
                      )}
                      {index + 1 < selectedTrip.legs.length ? (
                        !allTransits.includes(selectedTrip.legs[index + 1].mode) && <div className="flex w-11/12 border-t-ionic-light-gray border-t"></div>
                      ) : (
                        <div></div>
                      )}
                    </div>
                  </div>
                );
              })}
            <div className="flex items-center">
              <div className="w-1/12 flex items-center justify-center">{SwitchIcon('destination')}</div>
              <div className="flex w-11/12">
                <span className="text-xs max-w-[95%] text-ionic-secondary font-semibold">{selectedAddress != null ? selectedAddress.address : ''}</span>
                <span className="ml-auto text-right text-xs text-ionic-secondary font-semibold">
                  {timeToString(selectedTrip.legs[selectedTrip.legs.length - 1].endTime)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Bouton d'action */}
      <div style={{ height: '30%', display: 'none' }} className="mt-4 shadow-top-2 pt-3 cursor-pointer">
        <button className="bg-ionic-primary py-2 border-none w-full flex justify-center items-center rounded-lg p-1">
          <img src={CartIcon} className="mr-2 w-[18px]" />
          <span className="text-white text-sm">Réserver</span>
        </button>
      </div>
    </div>
  ) : (
    <CarSelection
      sourceDestination={selectedInputs}
      selectedItinerary={selectedItinerary}
      setSelectedItinerary={setSelectedItinerary}
      itineraries={itineraries}
      setItineraries={setItineraries}
      goItineraries={goItineraries}
      setGoItineraries={setGoItineraries}
      backupReturnItineraries={backupReturnItineraries}
      setBackupReturnItineraries={setBackupReturnItineraries}
      sourceDate={date}
    />
  );
};
export default TripList;
